'use client';
import { useEffect, useState } from 'react';
 import Link from'next/link';
 import Header from'@/components/common/Header';
 import Footer from'@/components/common/Footer';
 import Icon from'@/components/ui/AppIcon';

const modules = [
  {
    id: 'module_finance',
    name: 'Finance (FI/CO)',
    subModules: [
      {
        id: 'sub_fi_gl',
        name: 'General Ledger (FI-GL)',
        processes: ['Universal Journal', 'Document splitting', 'Parallel accounting', 'Real-time reporting'],
        integrations: ['Controlling (CO)', 'Asset Accounting (AA)', 'Accounts Payable/Receivable'],
      },
      {
        id: 'sub_fi_ar',
        name: 'Accounts Receivable (FI-AR)',
        processes: ['Customer invoicing', 'Payment processing', 'Credit management', 'Dunning'],
        integrations: ['Sales & Distribution', 'Cash Management', 'General Ledger'],
      },
      {
        id: 'sub_fi_ap',
        name: 'Accounts Payable (FI-AP)',
        processes: ['Vendor invoicing', 'Payment runs', 'Invoice verification', 'Expense management'],
        integrations: ['Materials Management', 'Cash Management', 'General Ledger'],
      },
      {
        id: 'sub_co',
        name: 'Controlling (CO)',
        processes: ['Cost center accounting', 'Profit center accounting', 'Product costing', 'Profitability analysis'],
        integrations: ['General Ledger', 'Production Planning', 'Sales & Distribution'],
      },
    ],
  },
  {
    id: 'module_scm',
    name: 'Supply Chain (MM/SD)',
    subModules: [
      {
        id: 'sub_mm',
        name: 'Materials Management (MM)',
        processes: ['Procurement', 'Inventory management', 'Goods receipt', 'Invoice verification'],
        integrations: ['Finance (AP)', 'Production Planning', 'Quality Management'],
      },
      {
        id: 'sub_sd',
        name: 'Sales & Distribution (SD)',
        processes: ['Order-to-cash', 'Pricing', 'Delivery processing', 'Billing'],
        integrations: ['Finance (AR)', 'Materials Management', 'Production Planning'],
      },
      {
        id: 'sub_wm',
        name: 'Warehouse Management (WM)',
        processes: ['Inbound/outbound processing', 'Stock placement', 'Picking', 'Physical inventory'],
        integrations: ['Materials Management', 'Sales & Distribution', 'Production Planning'],
      },
    ],
  },
  {
    id: 'module_manufacturing',
    name: 'Manufacturing (PP/QM)',
    subModules: [
      {
        id: 'sub_pp',
        name: 'Production Planning (PP)',
        processes: ['MRP', 'Production orders', 'Capacity planning', 'Shop floor control'],
        integrations: ['Materials Management', 'Sales & Distribution', 'Controlling'],
      },
      {
        id: 'sub_qm',
        name: 'Quality Management (QM)',
        processes: ['Quality planning', 'Quality inspection', 'Quality certificates', 'Quality notifications'],
        integrations: ['Materials Management', 'Production Planning', 'Sales & Distribution'],
      },
    ],
  },
  {
    id: 'module_hr',
    name: 'Human Resources (HCM)',
    subModules: [
      {
        id: 'sub_pa',
        name: 'Personnel Administration',
        processes: ['Organizational management', 'Personnel data', 'Time management', 'Payroll'],
        integrations: ['Finance (Controlling)', 'Travel Management', 'Talent Management'],
      },
    ],
  },
]

const additionalModules = [
  { id: 'add_pm', name: 'Plant Maintenance (PM)', href: '#pm' },
  { id: 'add_cs', name: 'Customer Service (CS)', href: '#cs' },
  { id: 'add_ps', name: 'Project Systems (PS)', href: '#ps' },
  { id: 'add_re', name: 'Real Estate (RE)', href: '#re' },
  { id: 'add_tr', name: 'Treasury (TR)', href: '#tr' },
  { id: 'add_ewm', name: 'Extended Warehouse (EWM)', href: '#ewm' },
]

export default function FunctionalPlaybookPage() {
  const [expandedModule, setExpandedModule] = useState<string | null>('module_finance')
  const [expandedSubModule, setExpandedSubModule] = useState<string | null>(null)

  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal')
    if (!reveals.length) return

    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    )
    reveals.forEach(el => observer.observe(el))

    return () => observer.disconnect()
  }, [])

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />

      <main className="pt-24">
        {/* Page Header */}
        <section className="mx-auto max-w-7xl px-6 py-16 border-b border-border">
          <div className="reveal">
            <h1 className="font-serif text-display-md md:text-display-lg font-bold mb-4">
              Functional Playbook
            </h1>
            <p className="font-mono text-lg text-muted-foreground max-w-3xl">
              Business processes, modules, and capabilities by function. What S/4HANA does for your business.
            </p>
          </div>
        </section>

        {/* Main Modules (Expandable Trees) */}
        <section className="mx-auto max-w-7xl px-6 py-16 border-b border-border">
          <div className="mb-10 reveal">
            <h2 className="font-serif text-3xl font-semibold mb-2">Core Modules</h2>
            <p className="font-mono text-sm text-muted-foreground">Primary business capabilities</p>
          </div>

          <div className="space-y-3">
            {modules.map((module) => (
              <div key={module.id} className="border border-border rounded reveal">
                {/* Module Header */}
                <button
                  onClick={() => setExpandedModule(expandedModule === module.id ? null : module.id)}
                  className="w-full p-6 text-left hover:bg-muted transition-colors flex items-center justify-between"
                >
                  <h3 className="font-serif text-2xl font-semibold">{module.name}</h3>
                  <Icon
                    name="ChevronDownIcon"
                    size={20}
                    className={`text-muted-foreground transition-transform ${
                      expandedModule === module.id ? 'rotate-180' : ''
                    }`}
                    variant="outline"
                  />
                </button>

                {/* Sub-modules */}
                {expandedModule === module.id && (
                  <div className="border-t border-border">
                    {module.subModules.map((sub) => (
                      <div key={sub.id} className="border-b border-border last:border-b-0">
                        <button
                          onClick={() => setExpandedSubModule(expandedSubModule === sub.id ? null : sub.id)}
                          className="w-full p-5 pl-10 text-left hover:bg-muted transition-colors flex items-center justify-between"
                        >
                          <h4 className="font-mono font-semibold">{sub.name}</h4>
                          <Icon
                            name="ChevronRightIcon"
                            size={16}
                            className={`text-muted-foreground transition-transform ${
                              expandedSubModule === sub.id ? 'rotate-90' : ''
                            }`}
                            variant="outline"
                          />
                        </button>

                        {expandedSubModule === sub.id && (
                          <div className="px-10 pb-5">
                            <div className="grid md:grid-cols-2 gap-6">
                              <div>
                                <h5 className="font-mono text-xs uppercase text-muted-foreground mb-3">Key Processes</h5>
                                <ul className="space-y-2">
                                  {sub.processes.map((process, idx) => (
                                    <li key={idx} className="font-mono text-sm flex items-start gap-2">
                                      <span className="w-1.5 h-1.5 bg-primary rounded-full mt-1.5"></span>
                                      <span>{process}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              <div>
                                <h5 className="font-mono text-xs uppercase text-muted-foreground mb-3">Integration Points</h5>
                                <ul className="space-y-2">
                                  {sub.integrations.map((integration, idx) => (
                                    <li key={idx} className="font-mono text-sm flex items-start gap-2">
                                      <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full mt-1.5"></span>
                                      <span>{integration}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Additional Modules */}
        <section className="mx-auto max-w-7xl px-6 py-16">
          <div className="mb-10 reveal">
            <h2 className="font-serif text-3xl font-semibold mb-2">Additional Modules</h2>
            <p className="font-mono text-sm text-muted-foreground">Extended capabilities and industry-specific functions</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 reveal">
            {additionalModules.map((module) => (
              <a
                key={module.id}
                href={module.href}
                className="p-5 border border-border rounded hover:bg-muted transition-colors flex items-center justify-between group"
              >
                <span className="font-mono font-semibold">{module.name}</span>
                <Icon
                  name="ArrowRightIcon"
                  size={16}
                  className="text-muted-foreground group-hover:text-foreground transition-colors"
                  variant="outline"
                />
              </a>
            ))}
          </div>

          <div className="mt-10 p-6 bg-card border border-border rounded reveal">
            <p className="font-mono text-sm text-muted-foreground">
              <strong className="text-foreground">Note:</strong> This playbook focuses on structural understanding and relationships between modules. For implementation details and configuration guidance, refer to SAP official documentation.
            </p>
          </div>
        </section>

        {/* Navigation CTA */}
        <section className="mx-auto max-w-7xl px-6 py-16 border-t border-border">
          <div className="text-center space-y-6 reveal">
            <p className="font-mono text-muted-foreground">
              Explore technical implementation details
            </p>
            <Link
              href="/technical-playbook"
              className="inline-block px-6 py-3 bg-primary text-primary-foreground font-mono text-sm rounded hover:opacity-90 transition-opacity"
            >
              Technical Playbook
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}